// Considered: EdgeCaseCalloutMolecule alone — rejected because it is a general-purpose callout
// with no status-pill, no reviewer-note slot, and no Edit&resubmit CTA. See spec Reuse Audit.

import React from 'react';
import { useTranslation } from 'localization';
import EdgeCaseCalloutMolecule, { EdgeCaseSeverity } from './EdgeCaseCalloutMolecule';
import StatusBadgeAtom, { StatusBadgeTone } from '../../atoms/StatusBadge/StatusBadgeAtom';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import './ChannelStatusCalloutMolecule.scss';

export type ChannelReviewStatus = 'draft' | 'pending_review' | 'rejected' | 'approved';

interface StatusMeta {
  tone: StatusBadgeTone;
  icon: string;
  severity: EdgeCaseSeverity;
  labelKey: string;
  titleKey: string;
  descKey: string;
}

const STATUS_META: Record<ChannelReviewStatus, StatusMeta> = {
  approved: {
    tone: 'success',
    icon: 'pi pi-check-circle',
    severity: 'success',
    labelKey: 'marketplace.channels.status.callout.approved.label',
    titleKey: 'marketplace.channels.status.callout.approved.title',
    descKey: 'marketplace.channels.status.callout.approved.desc',
  },
  draft: {
    tone: 'info',
    icon: 'pi pi-file-edit',
    severity: 'info',
    labelKey: 'marketplace.channels.status.callout.draft.label',
    titleKey: 'marketplace.channels.status.callout.draft.title',
    descKey: 'marketplace.channels.status.callout.draft.desc',
  },
  pending_review: {
    tone: 'warn',
    icon: 'pi pi-clock',
    severity: 'warn',
    labelKey: 'marketplace.channels.status.callout.pendingReview.label',
    titleKey: 'marketplace.channels.status.callout.pendingReview.title',
    descKey: 'marketplace.channels.status.callout.pendingReview.desc',
  },
  rejected: {
    tone: 'danger',
    icon: 'pi pi-times-circle',
    severity: 'danger',
    labelKey: 'marketplace.channels.status.callout.rejected.label',
    titleKey: 'marketplace.channels.status.callout.rejected.title',
    descKey: 'marketplace.channels.status.callout.rejected.desc',
  },
};

export interface ChannelStatusCalloutMoleculeProps {
  status: ChannelReviewStatus;
  /** Plain-text reviewer note shown only when status is 'rejected'. Never rendered as HTML. */
  reviewerNote?: string | null;
  /** When provided and status is 'rejected', renders the Edit & resubmit CTA. */
  onResubmit?: () => void;
  className?: string;
}

const ChannelStatusCalloutMolecule: React.FC<ChannelStatusCalloutMoleculeProps> = ({
  status,
  reviewerNote,
  onResubmit,
  className = '',
}) => {
  const { t } = useTranslation();
  const meta = STATUS_META[status];

  const calloutBody =
    status === 'rejected' && reviewerNote ? reviewerNote : t(meta.descKey);

  const rootClasses = ['marketplace-channel-status-callout', className]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={rootClasses}>
      <div className="marketplace-channel-status-callout__pill">
        <StatusBadgeAtom tone={meta.tone} icon={meta.icon} label={t(meta.labelKey)} size="small" />
      </div>
      <EdgeCaseCalloutMolecule
        severity={meta.severity}
        title={t(meta.titleKey)}
        body={calloutBody}
      />
      {status === 'rejected' && onResubmit && (
        <div className="marketplace-channel-status-callout__actions">
          <ButtonAtom
            label={t('marketplace.channels.status.callout.editResubmit')}
            icon="pi pi-pencil"
            iconPos="left"
            variant="secondary"
            size="small"
            onClick={onResubmit}
          />
        </div>
      )}
    </div>
  );
};

ChannelStatusCalloutMolecule.displayName = 'ChannelStatusCalloutMolecule';

export default ChannelStatusCalloutMolecule;
